import { Button, Descriptions, Result, Space, Spin, Tag, message } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import API from 'helpers/API';
import { HeadingH1 } from 'components/ui';
import { getOrderDisplayNo, getOrderStatus, getPaymentMethod } from 'utils/CodeMapper';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

const CUSTOMER_CANCELABLE_STATUS = 'RECEIVED';

const Page = () => {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState();
  const [isCanceling, setIsCanceling] = useState(false);
  const role = localStorage.getItem('@h2tms/role');

  useEffect(() => {
    let isMounted = true;

    const fetchOrder = async () => {
      setIsLoading(true);
      setFetchError(undefined);

      const response = await API.get(`/customer/orders/${rid}`);

      if (!isMounted) return;

      if (response?.success) {
        setOrder(response.data);
      } else {
        setOrder(undefined);
        setFetchError(getApiErrorMessage(response, '주문 상세 정보를 불러오지 못했습니다.'));
      }

      setIsLoading(false);
    };

    fetchOrder();

    return () => {
      isMounted = false;
    };
  }, [rid]);

  const doCancel = async () => {
    if (role !== 'CUSTOMER_MANAGER') return;
    if (order?.status !== CUSTOMER_CANCELABLE_STATUS) return;
    if (!window.confirm('정말 주문을 취소하시겠습니까?')) return;

    setIsCanceling(true);
    const response = await API.patch(`/customer/orders/${rid}/cancel`);

    if (response?.success) {
      message.success('주문 취소를 완료했습니다.');
      setOrder({
        ...order,
        status: 'CANCELED',
      });
    } else {
      message.error(getApiErrorMessage(response, '주문 취소를 실패했습니다.'));
    }

    setIsCanceling(false);
  };

  if (isLoading) {
    return (
      <Space direction="vertical" align="center" size={16} style={{ width: '100%', padding: '80px 0' }}>
        <Spin size="large" />
      </Space>
    );
  }

  if (fetchError || !order) {
    return (
      <Result
        status="error"
        title="주문 상세 정보를 불러오지 못했습니다."
        subTitle={fetchError}
        extra={[
          <Button key="back" onClick={() => navigate(-1)}>
            뒤로
          </Button>,
        ]}
      />
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <HeadingH1 title={'주문 상세'} />
      <Descriptions bordered column={2}>
        <Descriptions.Item label="주문번호">{getOrderDisplayNo(order)}</Descriptions.Item>
        <Descriptions.Item label="주문일자">{order.orderDate}</Descriptions.Item>
        <Descriptions.Item label="주문자 회사명">{order.customerCompany}</Descriptions.Item>
        <Descriptions.Item label="주문자명">{order.customerName}</Descriptions.Item>
        <Descriptions.Item label="주문자 전화번호">{order.customerPhone}</Descriptions.Item>
        <Descriptions.Item label="수량">{order.quantity}</Descriptions.Item>
        <Descriptions.Item label="결제방법">{getPaymentMethod(order.paymentMethod)}</Descriptions.Item>
        <Descriptions.Item label="상태">
          <Tag>{getOrderStatus(order.status)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="수신자명">{order.deliveryName}</Descriptions.Item>
        <Descriptions.Item label="수신자 회사명">{order.deliveryCompany}</Descriptions.Item>
        <Descriptions.Item label="수신자 전화번호">{order.deliveryPhone}</Descriptions.Item>
        <Descriptions.Item label="주소" span={2}>
          {order.deliveryAddress} {order.deliveryAddressDetail}
        </Descriptions.Item>
        <Descriptions.Item label="메모" span={2}>
          {order.memo || '-'}
        </Descriptions.Item>
      </Descriptions>
      <Space>
        <Button onClick={() => navigate(-1)}>뒤로</Button>
        {role === 'CUSTOMER_MANAGER' && order.status === CUSTOMER_CANCELABLE_STATUS && (
          <Button danger type="primary" loading={isCanceling} onClick={doCancel}>
            주문 취소
          </Button>
        )}
      </Space>
    </Space>
  );
};

export default Page;
